/**
 * Alertas (layout del rediseño AI Studio, 2026-10-15; antes UI v3 H3.2, DESIGN.md §5): lo principal es el resumen
 * diario (interruptor y prueba) y los últimos envíos; destinatario, remitente, contenido, reglas y baja van en un
 * desplegable. Suscripción, prueba, historial y baja sobre las rutas del bloque 6b. Honesta con el envío: el backend dice si el resumen y la prueba salen (`delivery`); mientras no salgan,
 * la suscripción se guarda igual (decisión del dueño, 13-10), una fila lo dice y la prueba queda deshabilitada con su
 * motivo. Nada de vista previa del correo: no hay endpoint para ella.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, ChevronDown, ChevronRight, Mail, SlidersHorizontal } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Chip, Columna, FOCO, Interruptor, Relleno } from '../components/v3/Ajustes';
import { notificationsService, AlertSubscription, DeliveryStatus, NotificationHistoryItem, TestResult } from '../services/notifications.service';
import { useAuth } from '../contexts/AuthContext';
import { fechaCorta, fechaLarga, hora } from '../utils/fechas';

const VISIBLES = 5;

const PRUEBA: Record<TestResult, { texto: string; ok: boolean }> = {
  enviado: { texto: 'Enviado a tu correo', ok: true },
  envio_inactivo: { texto: 'Los envíos aún no están activos', ok: false },
  sin_suscripcion: { texto: 'Tu cuenta debe estar aprobada y suscrita', ok: false },
  sin_documentos: { texto: 'No hay documentos recientes para la prueba', ok: false },
  error: { texto: 'No se pudo enviar; inténtalo de nuevo', ok: false },
};

export default function Notificaciones() {
  const { user } = useAuth();
  const [sub, setSub] = useState<AlertSubscription | null>(null);
  const [delivery, setDelivery] = useState<DeliveryStatus | null>(null);
  const [historial, setHistorial] = useState<NotificationHistoryItem[] | null>(null);
  const [error, setError] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState(false);
  const [prueba, setPrueba] = useState<'enviando' | TestResult | null>(null);
  const [confirmarBaja, setConfirmarBaja] = useState(false);
  const [verTodos, setVerTodos] = useState(false);
  const [configAbierta, setConfigAbierta] = useState(false);

  const cargar = useCallback(async () => {
    setError(false);
    setDelivery(null);
    setHistorial(null);
    try {
      const [estado, envios] = await Promise.all([notificationsService.getSubscription(), notificationsService.getHistory(50)]);
      setSub(estado.subscription);
      setDelivery(estado.delivery);
      setHistorial(envios);
    } catch {
      setError(true);
    }
  }, []);

  // Depende del id y no del objeto: recargar solo si cambia el usuario.
  const userId = user?.id;
  useEffect(() => {
    if (userId) cargar();
  }, [userId, cargar]);

  const cambiar = async (encender: boolean) => {
    const antes = sub;
    setGuardando(true);
    setErrorGuardar(false);
    setPrueba(null);
    if (sub) setSub({ ...sub, enabled: encender });
    try {
      setSub(await notificationsService.setEnabled(encender));
      setConfirmarBaja(false);
    } catch {
      setSub(antes);
      setErrorGuardar(true);
    } finally {
      setGuardando(false);
    }
  };

  const darDeBaja = async () => {
    setGuardando(true);
    setErrorGuardar(false);
    try {
      await notificationsService.unsubscribe();
      // 28-10: la baja apaga la fila (no la borra), para que la suscripción por defecto no la vuelva a crear.
      setSub(sub ? { ...sub, enabled: false } : null);
      setConfirmarBaja(false);
    } catch {
      setErrorGuardar(true);
    } finally {
      setGuardando(false);
    }
  };

  const enviarPrueba = async () => {
    setPrueba('enviando');
    try {
      setPrueba(await notificationsService.sendTest());
    } catch {
      setPrueba('error');
    }
  };

  const cargando = !error && delivery === null;
  const activo = Boolean(sub?.enabled);
  const salen = Boolean(delivery?.digest_enabled);
  const rotulo = guardando ? 'Guardando…' : errorGuardar ? 'No se pudo guardar' : !sub ? 'Sin suscripción' : !activo ? 'Pausado' : salen ? 'Activo' : 'Suscrito';
  const rotuloColor = errorGuardar ? 'text-red-700' : activo && salen && !guardando ? 'text-emerald-700' : 'text-slate-500';

  // Motivo por el que «Enviar» no está disponible (vacío = disponible).
  const motivoPrueba = !delivery?.test_available
    ? 'Disponible cuando se activen los envíos'
    : !activo
      ? 'Activa el resumen para probarlo'
      : '';

  const envios = historial || [];
  const mostrados = verTodos ? envios : envios.slice(0, VISIBLES);

  const pruebaBloqueada = Boolean(motivoPrueba) || prueba === 'enviando';
  const destinatario = sub?.email || user?.email || '';
  const ultimoEnvio = sub
    ? `${sub.last_notification_sent ? `${fechaLarga(sub.last_notification_sent)}, ${hora(sub.last_notification_sent)}` : 'Aún no hay envíos'} · suscrito desde el ${fechaLarga(sub.created_at).replace(/ de \d{4}$/, '')}`
    : '';
  const reintentar = `inline-flex items-center gap-1 text-[13px] font-medium text-[#4F46E5] hover:underline rounded ${FOCO}`;
  const accion = `inline-flex items-center gap-1 text-[13px] text-slate-600 hover:text-slate-900 rounded ${FOCO}`;

  return (
    <Layout title="Alertas">
      <Columna ancho={880}>
        {/* Encabezado: a la derecha, prueba e interruptor maestro */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3.5 min-w-0 flex-1 basis-[300px]">
            <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 bg-[#EEF2FF] text-[#4F46E5]" aria-hidden="true">
              <Bell className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-[16px] font-bold text-[#142D4E] leading-snug">Resumen diario del SII</h1>
              <p className="text-[12.5px] text-slate-500 mt-0.5 leading-snug">
                Lunes a viernes a las 9:00, después de la actualización del corpus
                {destinatario && !error && <> · Enviado a <span className="font-medium text-[#142D4E] break-words">{destinatario}</span></>}
              </p>
            </div>
          </div>

          {!cargando && !error && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={enviarPrueba}
                disabled={pruebaBloqueada}
                title={motivoPrueba || 'Te enviamos ahora un resumen con los últimos documentos, sin esperar a mañana'}
                className={`px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-[12px] font-medium flex items-center gap-1.5 transition-colors motion-reduce:transition-none ${FOCO} ${pruebaBloqueada ? 'text-slate-400 cursor-not-allowed' : 'text-slate-700 hover:bg-slate-50'}`}
              >
                <Mail className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />
                <span>{prueba === 'enviando' ? 'Enviando…' : 'Enviar prueba'}</span>
              </button>
              <div className="flex items-center gap-2">
                <span className={`text-[12.5px] font-semibold ${rotuloColor}`} aria-live="polite">{rotulo}</span>
                <Interruptor encendido={activo} onChange={cambiar} etiqueta="Resumen diario por correo" deshabilitado={guardando} />
              </div>
            </div>
          )}
        </div>

        {error && (
          <p className="text-[13px] text-slate-500">
            No se pudo cargar tu suscripción.{' '}
            <button type="button" onClick={cargar} className={reintentar}>Reintentar</button>
          </p>
        )}

        {/* Resultado de la prueba o el motivo por el que no está disponible */}
        {!error && !cargando && (prueba && prueba !== 'enviando' ? (
          <p className="-mt-3 mb-5 text-xs text-right">
            <span className={PRUEBA[prueba].ok ? 'text-emerald-700' : 'text-red-700'} role="status">{PRUEBA[prueba].texto}</span>
          </p>
        ) : motivoPrueba ? (
          <p className="-mt-3 mb-5 text-xs text-right text-slate-500">{motivoPrueba}</p>
        ) : null)}

        {!error && !cargando && !salen && (
          <div className="mb-6 flex items-center justify-between gap-3 rounded-xl bg-amber-50/60 px-4 py-3">
            <div className="min-w-0 text-[13px]">
              <div className="font-semibold text-[#142D4E]">Estado del envío</div>
              <div className="text-xs text-slate-600 mt-0.5">
                {activo
                  ? 'Los envíos aún no están activos. Tu suscripción queda guardada y el primer resumen llegará cuando los activemos.'
                  : 'Los envíos aún no están activos. Puedes suscribirte ya: el primer resumen llegará cuando los activemos.'}
              </div>
            </div>
            <Chip tono="warn">Detenido</Chip>
          </div>
        )}

        {!error && (
          <div className="space-y-6">
            {/* Lo principal: últimos envíos. Filas sin acción: no hay vista previa del correo. */}
            <div>
              <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 pb-2 border-b border-slate-100">Últimos envíos</h2>
              <div className="divide-y divide-slate-100 border-b border-slate-100">
                {cargando ? (
                  Array.from({ length: 4 }, (_, i) => (
                    <div key={i} className="py-3.5 space-y-2">
                      <Relleno ancho="60%" />
                      <Relleno ancho="35%" />
                    </div>
                  ))
                ) : envios.length === 0 ? (
                  <div className="py-3.5">
                    <div className="text-[14px] text-slate-500">Aún no hay envíos</div>
                    <div className="text-[12px] text-slate-500 mt-0.5">Los días sin novedades no aparecen: no se envía ni se registra nada.</div>
                  </div>
                ) : (
                  mostrados.map(e => {
                    const n = e.metadata?.document_count;
                    return (
                      <div key={e.id} className="py-3.5 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[14px] font-bold text-[#142D4E] leading-snug truncate" title={e.subject}>{e.subject}</div>
                          <div className="text-[12px] text-slate-500 mt-0.5">
                            {[fechaCorta(e.sent_at), hora(e.sent_at), n != null ? `${n} documento${n === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        <div className="shrink-0">
                          {e.status === 'sent' ? <Chip tono="ok">Enviado</Chip> : e.status === 'failed' ? <Chip tono="bad">Falló</Chip> : <Chip tono="info">Pendiente</Chip>}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {!cargando && envios.length > VISIBLES && (
                <div className="pt-2.5">
                  <button type="button" onClick={() => setVerTodos(v => !v)} className={accion} aria-expanded={verTodos}>
                    {verTodos ? 'Ver menos' : `Ver todos (${envios.length})`} <ChevronRight className={`w-3.5 h-3.5 transition-transform motion-reduce:transition-none ${verTodos ? '-rotate-90' : ''}`} aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>

            {/* Apoyo: configuración técnica y reglas de entrega, plegada */}
            {!cargando && (
              <div className="border-t border-slate-100 pt-3">
                <button
                  type="button"
                  onClick={() => setConfigAbierta(v => !v)}
                  aria-expanded={configAbierta}
                  aria-controls="alertas-config"
                  className={`flex items-center justify-between w-full py-2 text-left text-[13px] font-semibold text-slate-600 hover:text-[#142D4E] transition-colors motion-reduce:transition-none rounded ${FOCO}`}
                >
                  <span className="flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4 text-slate-400" aria-hidden="true" />
                    <span>Configuración técnica y reglas de entrega</span>
                  </span>
                  <span className="flex items-center gap-1 text-[12px] font-normal text-slate-500">
                    <span>{configAbierta ? 'Ocultar detalles' : 'Destinatario, remitente y baja'}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform motion-reduce:transition-none ${configAbierta ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </span>
                </button>

                {configAbierta && (
                  <div id="alertas-config" className="mt-2 p-4 rounded-2xl bg-slate-50/70 border border-slate-200/70 divide-y divide-slate-200/60 text-[12.5px]">
                    <div className="py-2.5 flex items-center justify-between gap-4">
                      <div className="flex gap-3 min-w-0">
                        <span className="w-24 shrink-0 font-bold text-[#142D4E]">Destinatario</span>
                        <span className="text-slate-600 break-words min-w-0">{`${destinatario} · el correo de tu cuenta`}</span>
                      </div>
                      <Link to="/perfil" className={`font-medium text-[#4F46E5] hover:underline shrink-0 rounded ${FOCO}`}>Ver perfil →</Link>
                    </div>

                    {delivery?.from && (
                      <div className="py-2.5 flex gap-3">
                        <span className="w-24 shrink-0 font-bold text-[#142D4E]">Remitente</span>
                        <span className="text-slate-600 break-words min-w-0">{delivery.from.replace(/"/g, '')}</span>
                      </div>
                    )}

                    <div className="py-2.5 flex gap-3">
                      <span className="w-24 shrink-0 font-bold text-[#142D4E]">Contenido</span>
                      <span className="text-slate-600">Oficios, circulares y resoluciones nuevos del SII, cada uno con número, fecha, título, resumen y enlace; un correo al día, lunes a viernes</span>
                    </div>

                    {sub && (
                      <div className="py-2.5 flex gap-3">
                        <span className="w-24 shrink-0 font-bold text-[#142D4E]">Último envío</span>
                        <span className="text-slate-600">{ultimoEnvio}</span>
                      </div>
                    )}

                    <div className="py-2.5 flex items-center justify-between gap-4">
                      <span className="text-slate-600">
                        {confirmarBaja ? 'Dejarás de recibir el resumen diario. ¿Confirmas?' : 'Los días sin novedades en el SII no generan correo ni registro en el historial.'}
                      </span>
                      {sub && (confirmarBaja ? (
                        <span className="flex items-center gap-3 shrink-0">
                          <button type="button" onClick={() => setConfirmarBaja(false)} className={`text-slate-600 hover:text-slate-900 rounded ${FOCO}`}>Cancelar</button>
                          <button type="button" onClick={darDeBaja} disabled={guardando} className={`font-medium rounded ${FOCO} ${guardando ? 'text-slate-400 cursor-not-allowed' : 'text-rose-600 hover:underline'}`}>Darme de baja</button>
                        </span>
                      ) : (
                        <button type="button" onClick={() => setConfirmarBaja(true)} className={`text-rose-600 hover:underline font-medium shrink-0 rounded ${FOCO}`}>Darme de baja</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Columna>
    </Layout>
  );
}
